import { Box, IconButton, Stack, Tooltip } from '@mui/material';
import { ContextMenuAction, CopyIcon, DeleteIcon, EditIcon } from '@schooly/style';
import { FC } from 'react';
import { useIntl } from 'react-intl';
import { DefaultStreamChatGenerics, StreamMessage } from 'stream-chat-react';

export const useMessageActions = (
  message: StreamMessage<DefaultStreamChatGenerics>,
  options: {
    onEdit?: (message: StreamMessage<DefaultStreamChatGenerics>) => void;
    onCopy?: (message: StreamMessage<DefaultStreamChatGenerics>) => void;
    onDelete?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  },
) => {
  const actions: ContextMenuAction[] = [
    options.onEdit
      ? {
          labelTextId: 'action-Edit',
          color: 'primary.main',
          icon: <EditIcon />,
          onClick: (onClose?: () => void) => {
            options.onEdit?.(message);
            onClose?.();
          },
        }
      : undefined,

    options.onCopy
      ? {
          labelTextId: 'action-Copy',
          color: 'primary.main',
          icon: <CopyIcon />,
          onClick: (onClose?: () => void) => {
            options.onCopy?.(message);
            onClose?.();
          },
        }
      : undefined,
    options.onDelete
      ? {
          labelTextId: 'action-Delete',
          color: 'error.main',
          icon: <DeleteIcon />,
          onClick: (onClose?: () => void, e?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            if (e) {
              options.onDelete?.(e);
              onClose?.();
            }
          },
        }
      : undefined,
  ].filter(Boolean) as ContextMenuAction[];

  return actions;
};

type MessageActionsProps = {
  actions: ContextMenuAction[];
  isHovered?: boolean;
  onClose?: () => void;
};

export const MessageActions: FC<MessageActionsProps> = ({
  actions,
  isHovered = false,
  onClose,
}) => {
  const { formatMessage } = useIntl();
  if (actions.length === 0) return null;

  const handleActionClick = (action: ContextMenuAction, e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    action.onClick(onClose, e);
  };

  return (
    <Tooltip
      open={isHovered}
      PopperProps={{ disablePortal: true }}
      componentsProps={{
        tooltip: {
          sx: (theme) => ({
            padding: 1,
            borderRadius: 0.5,
            margin: `${theme.spacing(0, 0, -0.5, 1)} !important`,
          }),
        },
      }}
      arrow={false}
      placement="top-start"
      title={
        <Stack direction="row" gap={0.5} alignItems="center">
          {actions.map((action) => (
            <Tooltip
              title={action.label ?? formatMessage({ id: action.labelTextId })}
              key={action.label ?? action.labelTextId}
              arrow={false}
              enterNextDelay={500}
              placement="top"
              componentsProps={{
                tooltip: {
                  sx: (theme) => ({
                    padding: theme.spacing(0.25, 0.5),
                    borderRadius: 0.5,
                    margin: `${theme.spacing(1.5)} !important`,
                  }),
                },
              }}
            >
              <IconButton
                onClick={(e) => handleActionClick(action, e)}
                inverse
                sx={{
                  '&:hover': {
                    color: action.color,
                  },
                }}
              >
                {action.icon}
              </IconButton>
            </Tooltip>
          ))}
        </Stack>
      }
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: 1,
          height: 1,
          zIndex: -1,
          backgroundColor: 'transparent',
        }}
      />
    </Tooltip>
  );
};
